import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Camera, X, Scan } from 'lucide-react';

interface BarcodeScannerProps {
  onScan: (code: string) => void;
  onClose: () => void;
  isOpen: boolean;
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ onScan, onClose, isOpen }) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string>('');
  const mountedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      // Delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (!mountedRef.current) {
          startScanner();
          mountedRef.current = true;
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      stopScanner();
      mountedRef.current = false;
    }

    return () => {
      stopScanner();
      mountedRef.current = false;
    };
  }, [isOpen]);

  const startScanner = async () => {
    // Prevent multiple initializations
    if (scannerRef.current) {
      console.log('Scanner already initialized');
      return;
    }

    try {
      setError('');
      
      // Check if element exists
      const element = document.getElementById('barcode-reader');
      if (!element) {
        throw new Error('Scanner element not found in DOM');
      }

      const scanner = new Html5Qrcode('barcode-reader');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' }, // Use back camera
        {
          fps: 10,
          qrbox: { width: 300, height: 300 }, // Increased scan area for better detection
          aspectRatio: 1.0,
          // Use proper enum constants for barcode format support
          formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,      // QR codes
            Html5QrcodeSupportedFormats.CODE_128,     // Code 128 (supplier barcodes)
            Html5QrcodeSupportedFormats.CODE_39,      // Code 39
            Html5QrcodeSupportedFormats.CODE_93,      // Code 93
            Html5QrcodeSupportedFormats.EAN_13,       // EAN-13 (standard product barcodes)
            Html5QrcodeSupportedFormats.EAN_8,        // EAN-8
            Html5QrcodeSupportedFormats.UPC_A,        // UPC-A
            Html5QrcodeSupportedFormats.UPC_E,        // UPC-E
            Html5QrcodeSupportedFormats.DATA_MATRIX,  // Data Matrix
            Html5QrcodeSupportedFormats.ITF,          // Interleaved 2 of 5
            Html5QrcodeSupportedFormats.CODABAR,      // Codabar
            Html5QrcodeSupportedFormats.PDF_417       // PDF417
          ],
          // Advanced settings for better detection
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true  // Use native browser API if available (better for Code 128 & QR)
          }
        },
        (decodedText) => {
          // Success callback
          console.log('Barcode scanned:', decodedText);
          onScan(decodedText);
          stopScanner();
          onClose();
        },
        (errorMessage) => {
          // Error callback (ignore continuous scan errors)
          // These are normal during scanning
        }
      );

      setIsScanning(true);
      console.log('Scanner started successfully');
    } catch (err: any) {
      console.error('Failed to start scanner:', err);
      setError(`Gagal mengakses kamera: ${err.message || 'Unknown error'}. Pastikan izin kamera diaktifkan.`);
      setIsScanning(false);
      scannerRef.current = null;
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        if (isScanning) {
          await scannerRef.current.stop();
          console.log('Scanner stopped');
        }
        await scannerRef.current.clear();
        scannerRef.current = null;
        setIsScanning(false);
      } catch (err) {
        console.error('Error stopping scanner:', err);
        // Force reset even on error
        scannerRef.current = null;
        setIsScanning(false);
      }
    }
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <Scan className="w-6 h-6 text-blue-600 mr-2" />
            <h2 className="text-xl font-bold text-gray-900">Scan Barcode/QR</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scanner Area */}
        <div className="p-6">
          {error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          ) : null}

          <div 
            id="barcode-reader" 
            className="rounded-lg overflow-hidden border-2 border-blue-300"
            style={{ width: '100%' }}
          />

          <div className="mt-4 text-center">
            <div className="flex items-center justify-center text-sm text-gray-600">
              <Camera className="w-4 h-4 mr-2" />
              <span>Arahkan kamera ke barcode atau QR code</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <button
            onClick={handleClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Tutup Scanner
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner;
